import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CircleCheck, PawPrint, ShieldCheck, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BreedSelect } from "@/components/BreedSelect";
import { api } from "@/lib/api";
import { addMonths } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const Input = ({ label, ...props }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
    <input
      {...props}
      className="mt-1.5 w-full rounded-2xl border border-ink/15 bg-cream px-4 py-2.5 text-sm text-ink placeholder:text-slate-400 focus:border-bark focus:outline-none focus:ring-2 focus:ring-bark/25 disabled:opacity-60"
    />
  </div>
);

const Area = ({ label, ...props }) => (
  <div className="sm:col-span-2">
    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
    <textarea
      {...props}
      rows={2}
      className="mt-1.5 w-full rounded-2xl border border-ink/15 bg-cream px-4 py-2.5 text-sm text-ink placeholder:text-slate-400 focus:border-bark focus:outline-none focus:ring-2 focus:ring-bark/25"
    />
  </div>
);

const STEPS = ["You", "Pup", "Health", "Records"];

const TEMPERAMENTS = [
  "Friendly with all", "Shy / needs slow intros", "High energy",
  "Prefers humans over dogs", "Senior / calm", "Anxious", "Playful", "Protective",
];
const tSlug = (t) => t.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");

const ID_NOTE =
  "Bookings are subject to ID verification. If your ID cannot be verified, your booking will be cancelled and you will be notified.";

export const OnboardingWizard = ({ open, onClose, onDone }) => {
  const { user, setUser } = useAuth();
  const [step, setStep] = useState(0);
  const [pup, setPup] = useState(null);
  const [busy, setBusy] = useState(false);
  const [parent, setParent] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    address: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    vet_name: "",
    vet_phone: "",
  });
  const [photoId, setPhotoId] = useState(null);
  const [basics, setBasics] = useState({
    name: "", breed: "", gender: "", neutered_spayed: false, weight_kg: "", date_of_birth: "", temperament: [],
  });
  const [health, setHealth] = useState({
    vaccination_last_date: "", vaccination_next_due: "", kennel_cough_date: "",
    tick_treatment_last: "", tick_treatment_next: "", deworming_last: "", deworming_next: "",
  });
  const [uploads, setUploads] = useState([]);

  const setP = (k) => (e) => setParent((f) => ({ ...f, [k]: e.target.value }));
  const setB = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setBasics((f) => ({ ...f, [k]: v }));
  };
  const toggleTemperament = (t) =>
    setBasics((f) => {
      const cur = Array.isArray(f.temperament) ? f.temperament : [];
      return { ...f, temperament: cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t] };
    });
  // Industry-standard intervals: vaccination yearly, tick & flea monthly, deworming quarterly
  const AUTO_NEXT = {
    vaccination_last_date: ["vaccination_next_due", 12],
    tick_treatment_last: ["tick_treatment_next", 1],
    deworming_last: ["deworming_next", 3],
  };
  const setH = (k) => (e) =>
    setHealth((f) => {
      const next = { ...f, [k]: e.target.value };
      const rule = AUTO_NEXT[k];
      if (rule && e.target.value && !f[rule[0]]) next[rule[0]] = addMonths(e.target.value, rule[1]);
      return next;
    });

  const uploadId = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/users/me/photo-id", fd);
      setPhotoId(r.data);
      toast.success("Photo ID uploaded — pending verification");
      e.target.value = "";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const saveParent = async () => {
    const missing = [];
    if (!parent.name.trim()) missing.push("name");
    if (!parent.phone.trim()) missing.push("phone number");
    if (!parent.address.trim()) missing.push("address");
    if (!parent.emergency_contact_name.trim()) missing.push("emergency contact name");
    if (!parent.emergency_contact_phone.trim()) missing.push("emergency contact number");
    if (missing.length) {
      toast.error(`Please add your ${missing.join(", ")}`);
      return;
    }
    if (!photoId) {
      toast.error("Please upload a photo ID to continue");
      return;
    }
    setBusy(true);
    try {
      await api.put("/users/me", parent);
      setStep(1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const saveBasics = async () => {
    const missing = [];
    if (!basics.name.trim()) missing.push("name");
    if (!String(basics.breed).trim()) missing.push("breed");
    if (!basics.gender) missing.push("gender");
    if (!basics.date_of_birth) missing.push("date of birth");
    if (basics.weight_kg === "" || basics.weight_kg === null) missing.push("weight");
    if (!(basics.temperament || []).length) missing.push("temperament (pick at least one)");
    if (missing.length) {
      toast.error(`Please add: ${missing.join(", ")}`);
      return;
    }
    setBusy(true);
    try {
      const r = pup
        ? await api.put(`/pups/${pup.pup_id}`, basics)
        : await api.post("/pups", basics);
      setPup(r.data);
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const saveHealth = async () => {
    if (!health.vaccination_last_date || !health.vaccination_next_due) {
      toast.error("Vaccination last-done and next-due months are needed so we can send timely reminders");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/pups/${pup.pup_id}`, health);
      setStep(3);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "vaccination");
      const r = await api.post(`/pups/${pup.pup_id}/documents`, fd);
      setUploads((u) => [...u, r.data]);
      toast.success("Vaccination record uploaded");
      e.target.value = "";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    if (uploads.length === 0) {
      toast.error(`Upload ${pup?.name || "your pup"}'s vaccination chart to finish`);
      return;
    }
    setBusy(true);
    try {
      await api.put("/users/me", { complete_onboarding: true });
      const me = await api.get("/auth/me");
      setUser(me.data);
      onDone();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not finish setup");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="onboarding-wizard" className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium text-ink">
            {step === 0 ? "Welcome! First, a little about you" : "Let's set up your pup's profile"}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            A quick {STEPS.length}-step setup so we can care for your pup and get you booking.
          </DialogDescription>
        </DialogHeader>

        <div className="mb-6 flex gap-2">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-1.5 rounded-full ${i <= step ? "bg-bark" : "bg-cream-dark"}`} />
              <p className={`mt-1.5 text-[10px] font-semibold uppercase tracking-wider ${i <= step ? "text-bark" : "text-slate-400"}`}>
                {i + 1}. {s}
              </p>
            </div>
          ))}
        </div>

        {step === 0 && (
          <div data-testid="onboard-step-parent" className="grid gap-4 sm:grid-cols-2">
            <Input label="Full name *" data-testid="onboard-parent-name" value={parent.name} onChange={setP("name")} placeholder="Your name" />
            <Input label="Email ID *" data-testid="onboard-parent-email" value={user?.email || ""} disabled readOnly />
            <Input label="Phone number *" data-testid="onboard-parent-phone" type="tel" value={parent.phone} onChange={setP("phone")} placeholder="98XXXXXXXX" />
            <Area label="Address *" data-testid="onboard-parent-address" value={parent.address} onChange={setP("address")} placeholder="Flat, street, area, city, PIN" />
            <Input label="Emergency contact name *" data-testid="onboard-emergency-name" value={parent.emergency_contact_name} onChange={setP("emergency_contact_name")} placeholder="Someone we can call if we can't reach you" />
            <Input label="Emergency contact number *" data-testid="onboard-emergency-phone" type="tel" value={parent.emergency_contact_phone} onChange={setP("emergency_contact_phone")} placeholder="98XXXXXXXX" />
            <Input label="Vet name" data-testid="onboard-vet-name" value={parent.vet_name} onChange={setP("vet_name")} placeholder="Dr. Rao (optional)" />
            <Input label="Vet phone number" data-testid="onboard-vet-phone" type="tel" value={parent.vet_phone} onChange={setP("vet_phone")} placeholder="98XXXXXXXX (optional)" />
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Photo ID *</label>
              {photoId ? (
                <div className="mt-1.5 flex items-center gap-2 rounded-2xl bg-emerald-50 px-4 py-3 text-xs font-semibold text-emerald-800">
                  <CircleCheck className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span className="truncate">{photoId.original_filename}</span>
                  <span className="ml-auto shrink-0 rounded-full bg-amberwarm/15 px-2.5 py-0.5 text-[10px] uppercase tracking-wider text-amberwarm">
                    Pending verification
                  </span>
                  <label className="shrink-0 cursor-pointer text-bark underline underline-offset-2">
                    Replace
                    <input type="file" accept="image/*,.pdf" className="hidden" onChange={uploadId} disabled={busy} />
                  </label>
                </div>
              ) : (
                <label
                  data-testid="onboard-id-upload-label"
                  className="mt-1.5 flex cursor-pointer flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-ink/15 bg-cream px-4 py-6 text-center transition-colors duration-200 hover:border-bark"
                >
                  <Upload className="h-6 w-6 text-bark" aria-hidden="true" />
                  <span className="text-sm font-semibold text-navy">{busy ? "Uploading…" : "Upload a photo ID"}</span>
                  <span className="text-xs text-slate-400">Aadhaar, PAN, Passport or Driving Licence · image or PDF, up to 10 MB</span>
                  <input data-testid="onboard-id-upload-input" type="file" accept="image/*,.pdf" className="hidden" onChange={uploadId} disabled={busy} />
                </label>
              )}
              <p className="mt-2 flex items-start gap-2 rounded-2xl bg-amberwarm/10 p-3 text-xs leading-relaxed text-slate-600">
                <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-bark" aria-hidden="true" />
                {ID_NOTE}
              </p>
            </div>
            <div className="sm:col-span-2">
              <button
                type="button"
                data-testid="onboard-next-parent"
                onClick={saveParent}
                disabled={busy}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-bark px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bark-dark disabled:opacity-60"
              >
                {busy ? "Saving…" : "Continue to your pup"}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div data-testid="onboard-step-basics" className="grid gap-4 sm:grid-cols-2">
            <Input label="Name *" data-testid="onboard-name" value={basics.name} onChange={setB("name")} placeholder="Bruno" />
            <div>
              <BreedSelect testid="onboard-breed" value={basics.breed} onChange={(v) => setBasics((f) => ({ ...f, breed: v }))} />
              <p className="mt-1 text-[11px] font-semibold text-bark">Required</p>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Gender *</label>
              <select
                data-testid="onboard-gender"
                value={basics.gender}
                onChange={setB("gender")}
                className="mt-1.5 w-full rounded-2xl border border-ink/15 bg-cream px-4 py-2.5 text-sm text-ink focus:border-bark focus:outline-none"
              >
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <Input label="Date of birth *" data-testid="onboard-dob" type="date" value={basics.date_of_birth} onChange={setB("date_of_birth")} />
            <Input label="Weight (kg) * — sets pricing tier" data-testid="onboard-weight" type="number" step="0.1" min="0" value={basics.weight_kg} onChange={setB("weight_kg")} placeholder="18" />
            <label className="flex items-center gap-3 self-end rounded-2xl border border-ink/15 bg-cream px-4 py-3 text-sm font-semibold text-navy">
              <input type="checkbox" data-testid="onboard-neutered" checked={basics.neutered_spayed} onChange={setB("neutered_spayed")} className="h-4 w-4 accent-[#EA580C]" />
              Neutered / spayed
            </label>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Temperament *</label>
              <p className="mt-0.5 text-xs text-slate-400">Pick all that apply — at least one.</p>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="onboard-temperament">
                {TEMPERAMENTS.map((t) => {
                  const on = (basics.temperament || []).includes(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      data-testid={`onboard-temperament-${tSlug(t)}`}
                      aria-pressed={on}
                      onClick={() => toggleTemperament(t)}
                      className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors duration-200 ${on ? "border-bark bg-bark-soft text-bark" : "border-ink/15 bg-white text-slate-500 hover:border-navy/40"}`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex gap-3 sm:col-span-2">
              <button
                type="button"
                onClick={() => setStep(0)}
                className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-6 py-3.5 text-sm font-semibold text-navy"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                Back
              </button>
              <button
                type="button"
                data-testid="onboard-next-basics"
                onClick={saveBasics}
                disabled={busy}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-bark px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bark-dark disabled:opacity-60"
              >
                {busy ? "Saving…" : "Continue to health details"}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="onboard-step-health" className="grid gap-4 sm:grid-cols-2">
            <p className="sm:col-span-2 rounded-2xl bg-cream-dark/60 p-4 text-xs leading-relaxed text-slate-600">
              Just the month &amp; year is fine — nobody remembers exact dates. Set a "last done"
              month and we'll pre-fill the next due for you (vaccination yearly, tick &amp; flea
              monthly, deworming every 3 months). We'll nudge you as the due month approaches.
            </p>
            <Input label="Vaccination (last) *" data-testid="onboard-vax-last" type="month" value={health.vaccination_last_date} onChange={setH("vaccination_last_date")} />
            <Input label="Vaccination next due *" data-testid="onboard-vax-due" type="month" value={health.vaccination_next_due} onChange={setH("vaccination_next_due")} />
            <Input label="Kennel cough vaccine" data-testid="onboard-kennel" type="month" value={health.kennel_cough_date} onChange={setH("kennel_cough_date")} />
            <Input label="Deworming next due" data-testid="onboard-deworm" type="month" value={health.deworming_next} onChange={setH("deworming_next")} />
            <Input label="Tick & flea (last)" data-testid="onboard-tick-last" type="month" value={health.tick_treatment_last} onChange={setH("tick_treatment_last")} />
            <Input label="Tick & flea next due" data-testid="onboard-tick-due" type="month" value={health.tick_treatment_next} onChange={setH("tick_treatment_next")} />
            <div className="flex gap-3 sm:col-span-2">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-6 py-3.5 text-sm font-semibold text-navy"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                Back
              </button>
              <button
                type="button"
                data-testid="onboard-next-health"
                onClick={saveHealth}
                disabled={busy}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-bark px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bark-dark disabled:opacity-60"
              >
                {busy ? "Saving…" : "Continue to records"}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="onboard-step-records">
            <p className="rounded-2xl bg-cream-dark/60 p-4 text-xs leading-relaxed text-slate-600">
              Attach {pup?.name}'s vaccination chart or card (image or PDF). This is required — we
              can't host a pup without a verified vaccination record.
            </p>
            <label
              data-testid="onboard-upload-label"
              className="mt-4 flex cursor-pointer flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-ink/15 bg-cream px-4 py-8 text-center transition-colors duration-200 hover:border-bark"
            >
              <Upload className="h-6 w-6 text-bark" aria-hidden="true" />
              <span className="text-sm font-semibold text-navy">{busy ? "Uploading…" : "Upload vaccination chart *"}</span>
              <span className="text-xs text-slate-400">Images or PDF, up to 10 MB</span>
              <input data-testid="onboard-upload-input" type="file" accept="image/*,.pdf" className="hidden" onChange={upload} disabled={busy} />
            </label>
            {uploads.length > 0 && (
              <ul className="mt-4 space-y-2">
                {uploads.map((u) => (
                  <li key={u.doc_id} className="flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-2.5 text-xs font-semibold text-emerald-800">
                    <CircleCheck className="h-4 w-4" aria-hidden="true" />
                    {u.original_filename}
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-6">
              <button
                type="button"
                data-testid="onboard-finish"
                onClick={finish}
                disabled={busy || uploads.length === 0}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-cream transition-colors duration-200 hover:bg-ink disabled:opacity-50"
              >
                <PawPrint className="h-4 w-4" aria-hidden="true" />
                {busy ? "Finishing…" : "Done — welcome to the pack"}
              </button>
              {uploads.length === 0 && (
                <p className="mt-3 text-center text-xs text-slate-400">
                  Upload at least one vaccination record to finish setup.
                </p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
